/**
 * How much premium is sitting in a contract, computed on the client.
 *
 * Everything here is arithmetic over fields the stable payload already carries, so adding
 * these columns costs no request. That is deliberate: the board is already the heaviest
 * consumer of the Theta budget, and a derived column must never become a fetch.
 *
 * `pool` values the whole contract -- open interest plus today's volume -- at the last
 * price. It is an approximation and worth being explicit about why: the honest figure is
 * the volume-weighted average of what was actually paid as the position was built, which
 * needs the full trade history of every open contract. Marking the whole pool at today's
 * last price answers a different, still useful question -- what the position is worth NOW
 * -- and it is the figure the desk asked for. It is not what was paid.
 *
 * There is deliberately no bought/sold split here. It needs volume classified as traded at
 * the bid or at the ask, and the stable chain snapshot carries neither: its `Bid Volume` /
 * `Ask Volume` are NBBO quote SIZES, and the `accumelated` variants arrive as 0 on every
 * row. A split built on either would be depth x price wearing the name of premium.
 */

function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

export interface ContractPremiumInputs {
  openInterest: unknown;
  volume: unknown;
  /** The live trade price. */
  last: unknown;
  /** The session's close, from the stable endpoint's `ohlc` snapshot. */
  priorClose?: unknown;
  /**
   * Value at the session's close instead of the live price.
   *
   * Outside regular hours the Volume on the row is the PRIOR session's, so multiplying it
   * by a live quote produces a figure that drifts all morning while nothing trades and
   * belongs to no session at all. Measured on SPXW 7775C: 6,792 traded on 08-24 at a 0.10
   * close, but the board priced it at 0.23 pre-market -- $152,820 against $67,920.
   */
  usePriorClose?: boolean;
}

export interface ContractPremiumFields {
  /** (open interest + volume) x last x 100 — the contract's whole pool at today's price. */
  pool: number | null;
  /**
   * Contracts today's volume PROVES are newly opened, because closing one requires one to
   * exist. Null unless volume exceeds open interest — below that, any mix of opens and
   * closes is consistent and nothing is provable.
   */
  provablyNewContracts: number | null;
  /** The provably-new contracts valued at the same price as the rest. */
  provablyNewPremium: number | null;
  /** Which price every figure above was valued at, so the UI can say so. */
  pricedAt: 'last' | 'prior_close' | null;
}

export function contractPremiumFields(input: ContractPremiumInputs): ContractPremiumFields {
  const oi = num(input.openInterest);
  const vol = num(input.volume);
  const last = num(input.last);
  const priorClose = num(input.priorClose);

  // Prefer the session's close when the volume being valued is that session's. Falls back
  // to whichever price exists, so a missing field degrades to the old behaviour rather than
  // blanking the column.
  const preferPrior = input.usePriorClose === true;
  const chosen = preferPrior ? (priorClose ?? last) : (last ?? priorClose);
  const price = chosen !== null && chosen > 0 ? chosen : null;
  const pricedAt: 'last' | 'prior_close' | null =
    price === null ? null : (price === priorClose && preferPrior ? 'prior_close' : 'last');

  const priced = price !== null;
  const pool = priced && (oi !== null || vol !== null)
    ? ((oi ?? 0) + (vol ?? 0)) * price * 100
    : null;


  let provablyNewContracts: number | null = null;
  if (oi !== null && vol !== null && vol > oi) provablyNewContracts = vol - oi;
  const provablyNewPremium =
    priced && provablyNewContracts !== null ? provablyNewContracts * price * 100 : null;

  return { pool, provablyNewContracts, provablyNewPremium, pricedAt };
}
